import React, { useState, useEffect } from "react";
import { useToast } from "@chakra-ui/react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { CREATEORG, UPDATEUSER } from "../../graphql/mutations";
import { USER, ORGANIZATION_TYPES } from "../../graphql/queries";
import { SearchSchoolComponent } from "../SearchSchoolComponent";
import { ModalBox } from "../ModalBox";
import { OrganizationForm } from "../OrganizationForm";
import { useOrganization } from "../../hooks/useOrganization";

export const AddOrgModal = ({ user, isTeacher, isOpen, onClose }) => {
  const [organizationTypes, setOrganizationTypes] = useState([]);
  const [organization, setOrganization] = useState({
    name: "",
    organizationType: {},
    street1: "",
    street2: "",
    city: "",
    state: "",
  });
  const {
    userOrganization,
    setShowNewOrgForm,
    addSelectedOrganization,
    updateIsAdding,
  } = useOrganization(user);
  const toast = useToast();

  const showToast = (title, status) => {
    toast({
      position: "top",
      title,
      status,
      duration: 9000,
      isClosable: true,
    });
  };

  const [getOrgTypes] = useLazyQuery(ORGANIZATION_TYPES, {
    onCompleted: (data) => {
      setOrganizationTypes(data.organizationTypes);
      setOrganization({
        ...organization,
        organizationType: {
          id: data.organizationTypes[0].id,
        },
      });
    },
    onError: (e) => {},
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  useEffect(() => {
    if (isTeacher) {
      getOrgTypes();
    }
  }, [getOrgTypes, isTeacher]);

  const [updateUser] = useMutation(UPDATEUSER, {
    update: (cache, { data }) => {
      const cached_user = cache.readQuery({
        query: USER,
        variables: {
          where: {
            username: { equals: user.username },
          },
        },
      });
      cache.writeQuery({
        query: USER,
        variables: {
          where: {
            username: { equals: user.username },
          },
        },
        data: {
          users: [
            {
              ...cached_user.users[0],
              organizations: data.updateUser.organizations,
            },
          ],
        },
      });
      addSelectedOrganization(data.updateUser.organizations[0]);
      showToast("Successfully added organization / school.", "success");
      onClose();
    },
    onError: (e) => {
      updateIsAdding(false);
      showToast(
        "Failed to add organization / school. Please try again.",
        "error"
      );
      onClose();
    },
  });

  const [createOrg] = useMutation(CREATEORG, {
    onCompleted: (data) => {
      updateUser({
        variables: {
          where: {
            id: user.id,
          },
          data: {
            organizations: {
              connect: [
                {
                  id: data.createOrganization.id,
                },
              ],
            },
          },
        },
      });
    },
    onError: (e) => {
      updateIsAdding(false);
      showToast(
        "Failed to add organization / school. Please try again.",
        "error"
      );
      onClose();
    },
  });

  const handleChange = (e) => {
    setOrganization({
      ...organization,
      ...(e.target.name === "organizationType"
        ? {
            organizationType: {
              ...organization.organizationType,
              id: e.target.value,
            },
          }
        : {
            [e.target.name]: e.target.value,
          }),
    });
  };

  const connectOrganization = (org) => {
    updateIsAdding(true);
    if (org.schoolType === "org") {
      updateUser({
        variables: {
          where: {
            id: user.id,
          },
          data: {
            organizations: {
              connect: [
                {
                  id: org.id,
                },
              ],
            },
          },
        },
      });
    } else {
      const organizationType = organizationTypes.find(
        (orgType) => orgType.name === org.organizationType
      );
      const elementary_school = organizationTypes.find(
        (orgType) => orgType.name === "Elementary School"
      );
      createNewOrg({
        name: org.schoolName,
        organizationType: {
          id: organizationType?.id ? organizationType.id : elementary_school.id,
        },
        street1: org?.street1,
        ...(org?.street2 ? { street2: org?.street2 } : {}),
        city: org?.city,
        state: org?.stateName,
        ...(org?.zip ? { zipcode: org?.zip } : {}),
        ...(org?.country ? { country: org?.country } : {}),
      });
    }
  };

  const createNewOrg = (data) => {
    updateIsAdding(true);
    createOrg({
      variables: {
        data: {
          name: data.name,
          email: user.email,
          organizationType: {
            connect: {
              id: data.organizationType.id,
            },
          },
          address: {
            create: {
              street1: data?.street1,
              ...(data?.street2 ? { street2: data?.street2 } : {}),
              city: data?.city,
              state: data?.state,
            },
          },
        },
      },
    });
  };
  return (
    <ModalBox
      title={
        userOrganization?.showNewOrgForm
          ? "Add your school or organization"
          : "Choose your school"
      }
      isOpen={isOpen}
      modalProps={{
        closeOnOverlayClick: false,
      }}
      onClose={onClose}
      size="xl"
    >
      {userOrganization?.showNewOrgForm ? (
        <OrganizationForm
          organization={organization}
          organizationTypes={organizationTypes}
          onCreate={createNewOrg}
          onCancel={() => setShowNewOrgForm(false)}
          isAdding={userOrganization.isAdding}
          handleChange={handleChange}
        />
      ) : (
        <SearchSchoolComponent
          addSelectedOrganization={connectOrganization}
          showNewOrgForm={setShowNewOrgForm}
          isAdding={userOrganization.isAdding}
        />
      )}
    </ModalBox>
  );
};
